/**
 * Patient Service
 *
 * API service for patient management operations
 */

import type { CopyDataResponse } from '@/services/certification/types';
import { clinicApiPath } from '@shared/lib/apiPath';
import { apiClient } from '@shared/lib/axios';
import {
  PatientFormData,
  PatientFormSchemaType,
  defaultHealthInsuranceCard,
  defaultMedicalAid,
  defaultPatientFormData,
  defaultPatientInformation,
} from '../types';

/** API `visit_last_year_month` (YYYY-MM) → form `patient.visit_last_day`. */
function parseVisitLastYearMonthFromApi(v: unknown): string | null {
  if (v == null || String(v).trim() === '') return null;
  const s = String(v).trim().slice(0, 7);
  return /^\d{4}-\d{2}$/.test(s) ? s : null;
}

/**
 * Transform raw API patient response to PatientFormData
 * For search results, this includes insurance cards from the API response
 */
function transformSearchResultToFormData(
  rawPatient: PatientListResponse['data']['data'][number]
): PatientFormData {
  const info = rawPatient.current_patient_information;

  // Extract insurance cards from the response if available
  const healthInsuranceCards = Array.isArray(rawPatient.health_insurance_cards)
    ? rawPatient.health_insurance_cards.map((card: any) => ({
        health_insurance_card_id: card.id,
        sub_number: card.sub_number,
        relationship_id: card.relationship_id,
        insurance_type_id: card.insurance_type_id,
        insurer_id: card.insurer_id,
        insurer_number: card.insurer_number || '',
        insurer_name: card.insurer_name || '',
        insurer_kind_name: card.insurer_kind_name || '',
        // `?? ''` (not `||`): patient_cost_rate = 0 is a legitimate saved value
        // (生活保護 full coverage / なし); `|| ''` would wipe it on load.
        patient_cost_rate: card.patient_cost_rate ?? '',
        patient_cost_id: card.patient_cost_id,
        elderly_insurance_certificate_id: card.elderly_insurance_certificate_id,
        card_mark: card.card_mark || '',
        card_number: card.card_number || '',
        qualification_date: card.qualification_date || '',
        expiration_date: card.expiration_date || '',
        name: card.name || '',
        name_kana: card.name_kana || '',
        sex_id: card.sex_id,
        date_of_birth: card.date_of_birth || '',
        postal_code1: card.postal_code1 || '',
        postal_code2: card.postal_code2 || '',
        address1: card.address1 || '',
        address2: card.address2 || '',
        address_kana: card.address_kana || '',
        telephone: card.telephone || '',
        claim_type_id: card.claim_type_id,
        // Preserve 0 when present; `|| ''` would incorrectly drop full-coverage rates.
        cost_rate: card.cost_rate ?? '',
        family_cost_rate: card.family_cost_rate ?? '',
        is_current: card.is_current || false,
    }))
    : [];
  const patientInformations = Array.isArray((rawPatient as any).patient_informations)
    ? (rawPatient as any).patient_informations.map((pi: any) => ({
        id: pi.id,
        patient_id: pi.patient_id,
        patient_information_id: pi.id,
        sub_number: pi.sub_number,
        name: pi.name || '',
        name_kana: pi.name_kana || '',
        sex_id: pi.sex_id,
        date_of_birth: pi.date_of_birth || '',
        postal_code1: pi.postal_code1 || '',
        postal_code2: pi.postal_code2 || '',
        address1: pi.address1 || '',
        address2: pi.address2 || '',
        address3: pi.address3 || '',
        address_name: pi.address_name || '',
        address_kana: pi.address_kana || '',
        telephone: pi.telephone || '',
        summary: pi.summary || '',
        remarks: pi.remarks || '',
        is_exclusion: !!pi.is_exclusion,
        is_non_output_telephone: !!pi.is_non_output_telephone,
        gaiji_check: !!pi.gaiji_check,
        address2_hidden: !!pi.address2_hidden,
        is_facility_resident: !!pi.is_facility_resident,
        address3_check: !!pi.address3_check,
        is_special_area_addition: !!pi.is_special_area_addition,
        available_from: pi.available_from ?? null,
        available_to: pi.available_to ?? null,
        is_current: !!pi.is_current,
      }))
    : [];

  // Handle case where current_patient_information is null
  const electric_treatment_is =
    (rawPatient as { electric_treatment_is?: number | null }).electric_treatment_is ?? null;
  const hot_treatment_is =
    (rawPatient as { hot_treatment_is?: number | null }).hot_treatment_is ?? null;
  const suddenly_visit_is =
    (rawPatient as { suddenly_visit_is?: number | null }).suddenly_visit_is ?? null;
  const insurance_type_id =
    (rawPatient as { insurance_type_id?: number | null }).insurance_type_id ??
    (Array.isArray(rawPatient.health_insurance_cards) &&
    rawPatient.health_insurance_cards.length > 0
      ? ((rawPatient.health_insurance_cards[0] as { insurance_type_id?: number })
          ?.insurance_type_id ?? null)
      : null);

  if (!info) {
    return {
      id: rawPatient.id,
      clinic_id: rawPatient.clinic_id || 0,
      serial_number: rawPatient.serial_number,
      official: rawPatient.official,
      electric_treatment_is,
      hot_treatment_is,
      suddenly_visit_is,
      insurance_type_id,
      default_address_name: rawPatient.default_address_name ?? null,
      current_patient_information: { ...defaultPatientInformation },
      patient_informations: patientInformations,
      health_insurance_cards: healthInsuranceCards,
      medical_aids: [],
    };
  }

  return {
    id: rawPatient.id,
    clinic_id: rawPatient.clinic_id || 0,
    serial_number: rawPatient.serial_number,
    official: rawPatient.official,
    electric_treatment_is,
    hot_treatment_is,
    suddenly_visit_is,
    insurance_type_id,
    default_address_name: rawPatient.default_address_name ?? null,
    current_patient_information: {
      patient_information_id: info.id,
      sub_number: info.sub_number,
      name: info.name,
      name_kana: info.name_kana,
      sex_id: info.sex_id,
      date_of_birth: info.date_of_birth,
      age: null,
      postal_code1: info.postal_code1,
      postal_code2: info.postal_code2,
      address1: info.address1,
      address2: info.address2,
      address3: info.address3,
      address_name: info.address_name,
      address_kana: info.address_kana,
      telephone: info.telephone,
      summary: info.summary,
      remarks: info.remarks,
      is_exclusion: info.is_exclusion,
      is_non_output_telephone: info.is_non_output_telephone,
      gaiji_check: false,
      address2_hidden: false,
      is_facility_resident: !!info.is_facility_resident,
      address3_check: false,
      is_special_area_addition: info.is_special_area_addition,
      available_from: info.available_from ?? null,
      available_to: '',
      is_current: info.is_current,
    },
    patient_informations: patientInformations,
    health_insurance_cards: healthInsuranceCards,
    medical_aids: [],
    // Legacy compatibility
    patient: {
      patient_id: rawPatient.id,
      serial_number: String(rawPatient.serial_number),
      official: rawPatient.official,
      visit_last_day: null,
      s_keyword: info.name,
    },
    patient_information: {
      patient_information_id: info.id,
      sub_number: info.sub_number,
      name: info.name,
      name_kana: info.name_kana,
      sex_id: info.sex_id,
      date_of_birth: info.date_of_birth,
      age: null,
      postal_code1: info.postal_code1,
      postal_code2: info.postal_code2,
      address1: info.address1,
      address2: info.address2,
      address3: info.address3,
      address_name: info.address_name,
      address_kana: info.address_kana,
      telephone: info.telephone,
      summary: info.summary,
      remarks: info.remarks,
      is_exclusion: info.is_exclusion,
      is_non_output_telephone: info.is_non_output_telephone,
      gaiji_check: false,
      address2_hidden: false,
      is_facility_resident: !!info.is_facility_resident,
      address3_check: false,
      is_special_area_addition: info.is_special_area_addition,
      available_from: info.available_from ?? null,
      available_to: '',
      is_current: info.is_current,
    },
    health_insurance_card: healthInsuranceCards[0] || {
      health_insurance_card_id: null,
      sub_number: null,
      relationship_id: info.relationship_id,
      insurance_type_id: null,
      insurer_id: null,
      insurer_number: '',
      insurer_name: '',
      insurer_kind_name: '',
      patient_cost_rate: '',
      patient_cost_id: null,
      elderly_insurance_certificate_id: null,
      card_mark: '',
      card_number: '',
      qualification_date: '',
      expiration_date: '',
      name: '',
      name_kana: '',
      sex_id: null,
      date_of_birth: '',
      postal_code1: '',
      postal_code2: '',
      address1: '',
      address2: '',
      address_kana: '',
      telephone: '',
      claim_type_id: null,
      cost_rate: '',
      family_cost_rate: '',
      is_current: false,
    },
    medical_aid: {
      medical_aid_id: null,
      sub_number: null,
      insurer_type_id: null,
      insurer_id: null,
      medical_aid_number: '',
      medical_aid_patient_number: '',
      insurer_cost_rate: '',
      is_partial_aid: false,
      is_special: false,
      calc_type: null,
      limit_amount: null,
      limit_num_month: null,
      limit_amount_month: null,
      application_start_date: '',
      application_end_date: '',
      is_end_medical_aid: false,
      is_current: false,
    },
  };
}

/**
 * Transform raw API patient response (from edit-init endpoint) to PatientFormData
 */
export function transformPatientEditToFormData(
  rawData: any // Raw API response with current_patient_information, arrays, etc.
): PatientFormData {
  const visitYm = parseVisitLastYearMonthFromApi(rawData.visit_last_year_month);
  const currentPi: PatientFormData['current_patient_information'] =
    rawData.current_patient_information ?? {
      ...defaultPatientInformation,
      sub_number: 1,
      is_current: true,
      patient_id: rawData.id,
    };

  return {
    ...defaultPatientFormData,
    id: rawData.id,
    clinic_id: rawData.clinic_id ?? 0,
    serial_number: rawData.serial_number,
    official: Boolean(rawData.official),
    electric_treatment_is: rawData.electric_treatment_is ?? undefined,
    insurance_type_id: rawData.insurance_type_id ?? undefined,
    current_patient_information: currentPi,
    patient_informations: rawData.patient_informations || [],
    health_insurance_cards: rawData.health_insurance_cards || [],
    medical_aids: (rawData.medical_aids || []).map((ma: any) => ({
      ...ma,
      insurer_name: ma.insurer_name || ma.insurer?.insurer_name || '',
    })),
    patient: {
      ...defaultPatientFormData.patient!,
      patient_id: rawData.id ?? null,
      serial_number: String(rawData.serial_number ?? ''),
      official: Boolean(rawData.official),
      visit_last_day: visitYm,
      s_keyword: currentPi?.name ?? '',
    },
  };
}

/**
 * Transform certification copy API response to PatientFormData.
 * Use when navigating to patient edit with state.copiedPatient after "保険証画面へコピー" (new patient).
 * Same shape as fetchPatientEdit so usePatientEditPage can use it for reset(formValues).
 */
export function transformCertificationCopyToFormData(
  copyResponse: CopyDataResponse
): PatientFormData {
  const pi = (copyResponse.patient_informations_data || {}) as Record<string, unknown>;
  const hc = (copyResponse.health_insurance_cards_data || {}) as Record<string, unknown>;
  const ma = (copyResponse.medical_aids_data || {}) as Record<string, unknown>;

  const current_patient_information = {
    ...defaultPatientInformation,
    patient_information_id: null,
    sub_number: 1,
    name: (pi.name as string) ?? '',
    name_kana: (pi.name_kana as string) ?? '',
    sex_id: (pi.sex_id as number) ?? null,
    date_of_birth: (pi.date_of_birth as string) ?? '',
    age: (pi.age as number) ?? null,
    postal_code1: (pi.postal_code1 as string) ?? '',
    postal_code2: (pi.postal_code2 as string) ?? '',
    address1: (pi.address1 as string) ?? '',
    address2: (pi.address2 as string) ?? '',
    address3: (pi.address3 as string) ?? '',
    address_name: (pi.address_name as string) ?? '',
    address_kana: (pi.address_kana as string) ?? '',
    telephone: (pi.telephone as string) ?? '',
    is_current: true,
  };

  const health_insurance_cards = [
    {
      ...defaultHealthInsuranceCard,
      health_insurance_card_id: null,
      sub_number: 1,
      relationship_id: (hc.relationship_id as number) ?? null,
      insurance_type_id: (hc.insurance_type_id as number) ?? null,
      insurer_id: (hc.insurer_id as number) ?? null,
      insurer_number: (hc.insurer_number as string) ?? '',
      insurer_name: (hc.insurer_name as string) ?? '',
      insurer_kind_name: (hc.insurer_kind_name as string) ?? '',
      patient_cost_rate: (hc.patient_cost_rate as number | string) ?? '',
      patient_cost_id: (hc.patient_cost_id as number) ?? null,
      elderly_insurance_certificate_id: (hc.elderly_insurance_certificate_id as number) ?? null,
      card_mark: (hc.card_mark as string) ?? '',
      card_number: (hc.card_number as string) ?? '',
      qualification_date: (hc.qualification_date as string) ?? '',
      expiration_date: (hc.expiration_date as string) ?? null,
      name: (hc.name as string) ?? '',
      name_kana: (hc.name_kana as string) ?? '',
      sex_id: (hc.sex_id as number) ?? null,
      date_of_birth: (hc.date_of_birth as string) ?? null,
      postal_code1: (hc.postal_code1 as string) ?? '',
      postal_code2: (hc.postal_code2 as string) ?? '',
      address1: (hc.address1 as string) ?? '',
      address2: (hc.address2 as string) ?? '',
      claim_type_id: (hc.claim_type_id as number) ?? null,
      cost_rate: (hc.cost_rate as string) ?? '',
      family_cost_rate: (hc.family_cost_rate as string) ?? '',
      is_current: true,
    },
  ];

  const medical_aids = [
    {
      ...defaultMedicalAid,
      medical_aid_id: null,
      sub_number: 1,
      insurer_type_id: (ma.insurer_type_id as number) ?? 1,
      insurer_id: (ma.insurer_id as number) ?? null,
      medical_aid_number: (ma.medical_aid_number as string) ?? '',
      medical_aid_patient_number: (ma.medical_aid_patient_number as string) ?? '',
      claim_type_id: (ma.claim_type_id as number) ?? null,
      application_start_date: (ma.application_start_date as string) ?? '',
      application_end_date: (ma.application_end_date as string) ?? '',
      is_current: true,
    },
  ];

  return {
    id: 0,
    clinic_id: 0,
    serial_number: 0,
    official: false,
    current_patient_information,
    patient_informations: [],
    health_insurance_cards,
    medical_aids,
  };
}

/**
 * API Response types
 */
interface PatientResponse {
  success: boolean;

  message: string;
  data: PatientFormData;
}

interface PatientListResponse {
  success: boolean;
  message: string;
  data: {
    data: {
      id: number;
      clinic_id: number;
      serial_number: number;
      official: boolean;
      current_patient_information: {
        id: number;
        patient_id: number;
        sub_number: number;
        is_current: boolean;
        name: string;
        name_kana: string;
        relationship_id: number | null;
        sex_id: number;
        date_of_birth: string;
        postal_code1: string;
        postal_code2: string;
        prefecture_id: number;
        address1: string;
        address2: string;
        address3: string;
        telephone: string;
        address_name: string;
        is_special_area_addition: boolean;
        address_kana: string;
        summary: string;
        remarks: string;
        is_exclusion: boolean;
        is_non_output_telephone: boolean;
        gaiji_check?: boolean;
        address2_hidden?: boolean;
        is_facility_resident?: boolean;
        address3_check?: boolean;
        available_from: string | null;
        available_to: string | null;
      };
      health_insurance_cards?: any[];
      default_address_name?: string | null;
      created_at: string;
      updated_at: string;
    }[];
  };
}

interface PatientActionResponse {
  success: boolean;
  message: string;
  data?: any;
}

/**
 * Get patient data for editing
 *
 * Route: GET /api/v1/clinic/{site_type}/{clinic_number}/patients/{id}/edit-init
 */
export async function fetchPatientEdit(
  siteType: string,
  clinicNumber: string,
  patientId: string
): Promise<PatientFormData> {
  try {
    const response = await apiClient.get<{
      success: boolean;
      message: string;
      data: any;
    }>(clinicApiPath(siteType, clinicNumber, `/patients/${patientId}/edit-init`));

    const transformedData = transformPatientEditToFormData(response.data.data);

    return transformedData;
  } catch (error) {
    console.error(`[fetchPatientEdit] Error fetching patient ${patientId}:`, error);
    throw error;
  }
}

/**
 * Create a new patient
 *
 * Route: POST /api/v1/clinic/{site_type}/{clinic_number}/patients
 */
export async function createPatient(
  siteType: string,
  clinicNumber: string,
  data: PatientFormSchemaType
): Promise<PatientFormData> {
  const response = await apiClient.post<PatientResponse>(
    clinicApiPath(siteType, clinicNumber, '/patients'),
    data
  );
  return transformPatientEditToFormData(response.data.data);
}

/**
 * Update an existing patient
 *
 * Route: PUT /api/v1/clinic/{site_type}/{clinic_number}/patients/{id}
 */
export async function updatePatient(
  siteType: string,
  clinicNumber: string,
  patientId: string,
  data: PatientFormSchemaType
): Promise<PatientFormData> {
  const response = await apiClient.put<PatientResponse>(
    clinicApiPath(siteType, clinicNumber, `/patients/${patientId}`),
    data
  );
  return transformPatientEditToFormData(response.data.data);
}

/**
 * Save patient as provisional (仮登録)
 *
 * Route: POST /api/v1/clinic/{site_type}/{clinic_number}/patients/{id}/provisional
 */
export async function provisionalSavePatient(
  siteType: string,
  clinicNumber: string,
  patientId: string | null,
  data: PatientFormSchemaType
): Promise<PatientFormData> {
  const raw = data as PatientFormSchemaType & { options?: Record<string, unknown> };
  const existingOptions = raw.options && typeof raw.options === 'object' ? { ...raw.options } : {};
  const dataWithMode = {
    ...data,
    options: {
      ...existingOptions,
      mode: 'provisional' as const,
    },
  };

  if (!patientId) {
    // Create new provisional patient
    return createPatient(siteType, clinicNumber, dataWithMode);
  }

  const response = await apiClient.post<PatientResponse>(
    clinicApiPath(siteType, clinicNumber, `/patients/${patientId}/provisional`),
    dataWithMode
  );
  return transformPatientEditToFormData(response.data.data);
}

/**
 * Delete a patient
 *
 * Route: DELETE /api/v1/{site_type}/{clinic_number}/patients/{id}
 */
export async function deletePatient(
  siteType: string,
  clinicNumber: string,
  patientId: string
): Promise<void> {
  await apiClient.delete<PatientActionResponse>(
    clinicApiPath(siteType, clinicNumber, `/patients/${patientId}`)
  );
}

/**
 * Search patients
 *
 * Route: GET /api/v1/clinic/{site_type}/{clinic_number}/patients/search
 */
export async function searchPatients(
  siteType: string,
  clinicNumber: string,
  keyword: string,
  includeExcluded: boolean = false,
  page: number = 1,
  perPage: number = 20,
  sortField: string = 'name_kana',
  sortOrder: 'asc' | 'desc' = 'asc',
  /** 訪問日 YYYY-MM-DD。指定時は API が electric_treatment_is / hot_treatment_is を返す */
  visitDate?: string | null,
  /** A2 or R2 — API uses this to attach electric_treatment_is (A2) or hot_treatment_is (R2) */
  serviceType?: string,
  /** 施術入力検索用: official=1（本登録）のみ返す */
  officialOnly?: boolean
): Promise<{
  data: PatientFormData[];
  pagination: {
    current_page: number;
    last_page: number;
    per_page: number;
    total: number;
    from: number | null;
    to: number | null;
  };
}> {
  try {
    const response = await apiClient.get<{
      success: boolean;
      message: string;
      data: {
        data: any[];
        pagination: {
          current_page: number;
          last_page: number;
          per_page: number;
          total: number;
          from: number | null;
          to: number | null;
        };
      };
    }>(clinicApiPath(siteType, clinicNumber, '/patients/search'), {
      params: {
        keyword,
        include_exclusion: includeExcluded,
        page,
        per_page: perPage,
        ...(sortField && { sort_field: sortField }),
        ...(sortField && { sort_order: sortOrder }),
        ...(visitDate && { visit_date: visitDate }),
        ...(serviceType && { service_type: serviceType.toLowerCase() }),
        ...(officialOnly && { official_only: true }),
        with: 'health_insurance_cards',
      },
    });

    if (
      !response.data.data ||
      !response.data.data.data ||
      !Array.isArray(response.data.data.data)
    ) {
      console.error('Invalid response format:', response.data);
      throw new Error('Invalid API response format');
    }

    const transformed = response.data.data.data.map((patient, index) => {
      try {
        return transformSearchResultToFormData(patient);
      } catch (error) {
        console.error(`Error transforming patient ${index}:`, error, patient);
        throw error;
      }
    });

    return {
      data: transformed,
      pagination: response.data.data.pagination,
    };
  } catch (error) {
    console.error('Search patient error:', error);
    throw error;
  }
}

/**
 * Get master data for patient form (sex types, insurance types, etc.)
 *
 * Route: GET /api/v1/clinic/{site_type}/{clinic_number}/patients/master-data
 */
export async function getPatientMasterData(siteType: string, clinicNumber: string): Promise<any> {
  const response = await apiClient.get<PatientResponse>(
    clinicApiPath(siteType, clinicNumber, '/patients/master-data')
  );
  return response.data.data;
}

/**
 * Switch patient history
 *
 * Route: POST /api/v1/clinic/{site_type}/{clinic_number}/patients/{id}/histories/switch
 */
export async function switchPatientHistory(
  siteType: string,
  clinicNumber: string,
  patientId: number,
  type: 'patient_information' | 'health_insurance_card' | 'medical_aid',
  historyId: number
): Promise<PatientResponse> {
  const response = await apiClient.post<PatientResponse>(
    clinicApiPath(siteType, clinicNumber, `/patients/${patientId}/histories/switch`),
    {
      type,
      history_id: historyId,
    }
  );
  return response.data;
}

/**
 * Delete patient history
 *
 * Route: DELETE /api/v1/clinic/{site_type}/{clinic_number}/patients/{id}/histories/{type}/{historyId}
 */
export async function deleteHistory(
  siteType: string,
  clinicNumber: string,
  patientId: number,
  type: 'patient_information' | 'health_insurance_card' | 'medical_aid',
  historyId: number
): Promise<PatientActionResponse> {
  const response = await apiClient.delete<PatientActionResponse>(
    clinicApiPath(siteType, clinicNumber, `/patients/${patientId}/histories/${type}/${historyId}`)
  );
  return response.data;
}

export interface PatientInformationPeriodPayload {
  id: number;
  available_from: string | null;
  available_to: string | null;
}

/**
 * Save 有効期限FROM/TO of every 住所情報履歴 row (受診者履歴「編集」).
 *
 * Route: PUT /api/v1/clinic/{site_type}/{clinic_number}/patients/{id}/histories/patient-information-periods
 */
export async function updatePatientInformationPeriods(
  siteType: string,
  clinicNumber: string,
  patientId: number,
  periods: PatientInformationPeriodPayload[]
): Promise<PatientResponse> {
  const response = await apiClient.put<PatientResponse>(
    clinicApiPath(
      siteType,
      clinicNumber,
      `/patients/${patientId}/histories/patient-information-periods`
    ),
    { periods }
  );
  return response.data;
}

/**
 * Validate bulk history qualification date (A2 getAvailableError).
 * Called from the modal before closing to show errors inline.
 *
 * Route: POST /api/v1/clinic/{site_type}/{clinic_number}/patients/{id}/histories/validate-bulk-date
 */
export async function validateBulkHistoryDate(
  siteType: string,
  clinicNumber: string,
  patientId: number,
  qualificationDate: string,
  mode: 'bulk' | 'patient_information' = 'bulk'
): Promise<void> {
  await apiClient.post(
    clinicApiPath(siteType, clinicNumber, `/patients/${patientId}/histories/validate-bulk-date`),
    { qualification_date: qualificationDate, mode }
  );
}

/**
 * Bulk create patient history
 *
 * Route: POST /api/v1/clinic/{site_type}/{clinic_number}/patients/{id}/histories/bulk-create
 */
export async function bulkCreatePatientHistory(
  siteType: string,
  clinicNumber: string,
  patientId: number,
  data: PatientFormSchemaType & {
    options?: {
      include_medical_aid_when_none?: boolean;
    };
  }
): Promise<PatientResponse> {
  const response = await apiClient.post<PatientResponse>(
    clinicApiPath(siteType, clinicNumber, `/patients/${patientId}/histories/bulk-create`),
    data
  );
  return response.data;
}

/**
 * Search insurers by insurer number
 *
 * Route: GET /api/v1/clinic/{site_type}/{clinic_number}/insurers/search?insurer_number={number}
 */
export interface InsurerSearchResponse {
  data: Array<{
    id: number;
    insurer_number: string;
    insurer_kind_name: string;
    insurer_name: string;
    insurance_type_id: number;
    has_payment_history: boolean;
  }>;
}

export async function searchInsurers(
  siteType: string,
  clinicNumber: string,
  insurerNumber: string
): Promise<InsurerSearchResponse> {
  const response = await apiClient.get<InsurerSearchResponse>(
    clinicApiPath(siteType, clinicNumber, '/insurers/search'),
    {
      params: {
        insurer_number: insurerNumber,
      },
    }
  );
  return response.data;
}

/**
 * Fetch insurer detail by ID (for form fill after search list selection).
 * Route: GET /api/v1/clinic/{site_type}/{clinic_number}/insurers/{id}
 */
export interface InsurerDetailApiResponse {
  success: boolean;
  data: {
    id: number;
    insurer_number: string | null;
    sub_number?: number | null;
    insurer_name: string | null;
    insurer_type_id: number | null;
    insurer_type: { id: number; name: string } | null;
    cost_rate: number | null;
    family_cost_rate: number | null;
    medix_claim_type_id?: number | null;
    [key: string]: unknown;
  };
}

export async function fetchInsurerDetail(
  siteType: string,
  clinicNumber: string,
  id: number
): Promise<InsurerDetailApiResponse['data']> {
  const response = await apiClient.get<InsurerDetailApiResponse>(
    clinicApiPath(siteType, clinicNumber, `/insurers/${id}`)
  );
  return response.data.data;
}

/**
 * Fetch patient lookup data (dropdown options for master data)
 *
 * Route: GET /api/v1/clinic/{site_type}/{clinic_number}/patients/master-data
 */
export interface PatientLookupDataResponse {
  success: boolean;
  data: {
    sexes?: Array<{ id: number; name: string }>;
    relationships?: Array<{ id: number; name: string }>;
    insurance_types?: Array<{ id: number; name: string }>;
    patient_costs?: Array<{ id: number; name: string }>;
    elderly_insurance_certificates?: Array<{ id: number; name: string }>;
    insurer_types?: Array<{ id: number; name: string }>;
    treatment_contents?: Array<{ id: number; name: string; value?: string | null; sortno: number }>;
  };
}

export async function fetchPatientLookupData(
  siteType: string,
  clinicNumber: string
): Promise<PatientLookupDataResponse> {
  const response = await apiClient.get<PatientLookupDataResponse>(
    clinicApiPath(siteType, clinicNumber, '/patients/master-data')
  );
  return response.data;
}

export type { PatientActionResponse, PatientListResponse, PatientResponse };
